import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { IconType } from '@shared/components';
import { Permission } from '@app/store/session/models';

export interface SidebarMenuItem {
    label: string;
    /** Shown beside the label on the dashboard's shortcut list; items without one are left out of it. */
    description?: string;
    icon?: IconType;
    routerLink?: string;
    children?: SidebarMenuItem[];
    pinToBottom?: boolean;
    collapsed?: boolean;
    /** Active only on its own path, not on the paths below it — `/stock` would otherwise stay lit on `/stock/history`. */
    exact?: boolean;
    /** The permission the route's `hasPermission()` guard checks; the entry is hidden without it. */
    permission?: Permission;
    testId?: string;
}

export const MENU_ITEMS: SidebarMenuItem[] = [
    { label: marker('sidebar.dashboard'), icon: 'house', routerLink: '/dashboard', testId: 'nav-dashboard' },
    {
        label: marker('sidebar.stock'),
        children: [
            {
                label: marker('sidebar.my-stock'),
                description: marker('stock.menu.description'),
                icon: 'warehouse',
                routerLink: '/stock',
                exact: true,
                testId: 'nav-my-stock',
            },
            {
                label: marker('sidebar.my-history'),
                description: marker('stock-history.mine.description'),
                icon: 'history',
                routerLink: '/stock/history',
                testId: 'nav-my-history',
            },
        ],
    },
    {
        label: marker('sidebar.administration'),
        children: [
            {
                label: marker('sidebar.users'),
                description: marker('users.description'),
                icon: 'user',
                routerLink: '/users',
                permission: 'users.read',
                testId: 'nav-users',
            },
            {
                label: marker('sidebar.divisions'),
                description: marker('divisions.description'),
                icon: 'columns-3',
                routerLink: '/divisions',
                permission: 'divisions.read',
                testId: 'nav-divisions',
            },
            {
                label: marker('sidebar.departments'),
                description: marker('departments.description'),
                icon: 'list',
                routerLink: '/departments',
                permission: 'departments.read',
                testId: 'nav-departments',
            },
            {
                label: marker('sidebar.roles'),
                description: marker('roles.description'),
                icon: 'shield',
                routerLink: '/roles',
                permission: 'roles.read',
                testId: 'nav-roles',
            },
            {
                label: marker('sidebar.warehouses'),
                description: marker('warehouses.description'),
                icon: 'warehouse',
                routerLink: '/warehouses',
                permission: 'warehouses.read',
                testId: 'nav-warehouses',
            },
            {
                label: marker('sidebar.stock-history'),
                description: marker('stock-history.all.description'),
                icon: 'history',
                routerLink: '/stock-history',
                permission: 'stock_transfers.read',
                testId: 'nav-stock-history',
            },
            {
                label: marker('sidebar.stock-supply'),
                description: marker('stock.supply.menu.description'),
                icon: 'package-plus',
                routerLink: '/stock/supply',
                permission: '*',
                testId: 'nav-stock-supply',
            },
            {
                label: marker('sidebar.materials'),
                description: marker('materials.description'),
                icon: 'gem',
                routerLink: '/materials',
                permission: 'materials.read',
                testId: 'nav-materials',
            },
            {
                label: marker('sidebar.elements'),
                description: marker('elements.description'),
                icon: 'atom',
                routerLink: '/elements',
                permission: 'elements.read',
                testId: 'nav-elements',
            },
        ],
    },
    {
        label: marker('sidebar.storytelling'),
        pinToBottom: true,
        collapsed: true,
        children: [
            { label: marker('sidebar.storytelling.controls'), icon: 'columns-3', routerLink: '/storytelling/controls', testId: 'nav-storytelling-controls' },
            { label: marker('sidebar.storytelling.buttons'), icon: 'square', routerLink: '/storytelling/buttons', testId: 'nav-storytelling-buttons' },
            { label: marker('sidebar.storytelling.colors'), icon: 'palette', routerLink: '/storytelling/colors', testId: 'nav-storytelling-colors' },
            {
                label: marker('sidebar.storytelling.typography'),
                icon: 'align-left',
                routerLink: '/storytelling/typography',
                testId: 'nav-storytelling-typography',
            },
            { label: marker('sidebar.storytelling.grid'), icon: 'layout-grid', routerLink: '/storytelling/grid', testId: 'nav-storytelling-grid' },
            { label: marker('sidebar.storytelling.icons'), icon: 'star', routerLink: '/storytelling/icons', testId: 'nav-storytelling-icons' },
            {
                label: marker('sidebar.storytelling.messages'),
                icon: 'messages-square',
                routerLink: '/storytelling/messages',
                testId: 'nav-storytelling-messages',
            },
            { label: marker('sidebar.storytelling.table'), icon: 'list', routerLink: '/storytelling/table', testId: 'nav-storytelling-table' },
        ],
    },
];

/** The menu with the entries the user lacks a permission for removed; a section with no entries left goes with them. */
export function visibleMenuItems(hasPermission: (permission: Permission) => boolean): SidebarMenuItem[] {
    return MENU_ITEMS.map((item) => {
        return item.children ? { ...item, children: item.children.filter((child) => !child.permission || hasPermission(child.permission)) } : item;
    }).filter((item) => !item.children || item.children.length > 0);
}
